import { v4 as uuidv4 } from 'uuid';
import Papa from 'papaparse';
import emailTemplateTokenContract from '@/contracts/email-template-token-contract.json';

// Parse CSV text into rows of trimmed string cells.
//
// Uses papaparse (already the parser for components/CustomCSVImporter) so quoted
// fields containing commas OR newlines are handled per RFC-4180. The previous
// hand-rolled implementation split on '\n' before tokenizing, which corrupted any
// row with a multi-line quoted cell (common in CRM exports) and silently produced
// empty upload payloads.
function parseCsvRows(csvContent: string): string[][] {
  const { data } = Papa.parse<string[]>(csvContent, {
    skipEmptyLines: 'greedy',
  });
  return (data as string[][]).map((row) => row.map((cell) => (cell ?? '').trim()));
}

// Normalize a header/key for tolerant matching between AI-returned mapping keys and
// the actual CSV headers — case-, whitespace-, and punctuation-insensitive.
function normalizeHeader(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

// Pragmatic email shape check — used to rescue a valid email when the AI maps an empty
// email column to `email` while the populated one lands elsewhere. Not RFC-perfect by
// design; it only needs to distinguish "looks like an email" from "blank/other text".
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Reconcile AI field_mappings (keyed by the column names the AI returned) against the
 * actual CSV headers, returning a `header -> standardField` map.
 *
 * The AI is asked to echo column names verbatim, but LLMs reformat them (casing,
 * spacing, punctuation) and sometimes return a shortened variant ("Email" for a
 * header named "Email Address"). An exact-string lookup misses in those cases and
 * every row maps to nothing — the empty-upload-payload bug. Resolution runs in
 * three passes, each consuming an AI key so it cannot be reused:
 *   1. exact key match
 *   2. normalized-equality match (case/space/punctuation-insensitive)
 *   3. unique containment match — a header resolves to an AI key only when exactly
 *      one unused key is a normalized substring of the header (or vice versa).
 *      Uniqueness prevents ambiguous guesses (e.g. two "...Email" columns).
 */
export function resolveHeaderMappings(
  headers: string[],
  fieldMappings: { [key: string]: string },
): { [header: string]: string } {
  const resolved: { [header: string]: string } = {};
  const usedKeys = new Set<string>();
  const keys = Object.keys(fieldMappings);

  // Pass 1 & 2: exact, then normalized equality.
  for (const header of headers) {
    if (fieldMappings[header]) {
      resolved[header] = fieldMappings[header];
      usedKeys.add(header);
      continue;
    }
    const target = normalizeHeader(header);
    const hit = keys.find((key) => !usedKeys.has(key) && normalizeHeader(key) === target);
    if (hit) {
      resolved[header] = fieldMappings[hit];
      usedKeys.add(hit);
    }
  }

  // Pass 3: unique normalized containment for still-unresolved headers.
  for (const header of headers) {
    if (resolved[header]) continue;
    const target = normalizeHeader(header);
    if (!target) continue;
    const matches = keys.filter((key) => {
      if (usedKeys.has(key)) return false;
      const nk = normalizeHeader(key);
      return nk.length > 0 && (target.includes(nk) || nk.includes(target));
    });
    if (matches.length === 1) {
      resolved[header] = fieldMappings[matches[0]];
      usedKeys.add(matches[0]);
    }
  }

  return resolved;
}

/**
 * Resolve the standard field a single CSV header maps to. Thin wrapper over
 * {@link resolveHeaderMappings} for call sites that only have one header in hand.
 */
export function findStandardField(
  header: string,
  fieldMappings: { [key: string]: string },
): string | undefined {
  return resolveHeaderMappings([header], fieldMappings)[header];
}

export interface CsvFieldMapping {
  detected_type: 'person' | 'company';
  field_mappings: {
    [csvColumnName: string]: string; // Maps CSV column to our standard field names
  };
  confidence: number; // 0-1 confidence score
  reasoning?: string; // Optional explanation
  suggested_filename?: string; // AI-generated contextual filename
  suggested_title?: string; // AI-generated descriptive title
}

export type CsvTemplateTokenPolicy = 'block' | 'warn';

const TOKEN_CONTRACT_ENTRIES = emailTemplateTokenContract.tokens as Array<{
  token: string;
  policy: CsvTemplateTokenPolicy;
}>;

export const RESERVED_CORE_TEMPLATE_TOKENS = TOKEN_CONTRACT_ENTRIES.map(({ token }) => token);
export const BLOCKING_TEMPLATE_TOKENS = TOKEN_CONTRACT_ENTRIES
  .filter(({ policy }) => policy === 'block')
  .map(({ token }) => token);
export const WARNING_ONLY_TEMPLATE_TOKENS = TOKEN_CONTRACT_ENTRIES
  .filter(({ policy }) => policy === 'warn')
  .map(({ token }) => token);

const TEMPLATE_TOKEN_POLICY_BY_NAME = new Map(
  TOKEN_CONTRACT_ENTRIES.map(({ token, policy }) => [token, policy]),
);
const SLUG_NON_ALPHANUMERIC_RE = new RegExp(
  emailTemplateTokenContract.slug_normalization.non_alphanumeric_pattern,
  'g',
);
const SLUG_TRIM_RE = new RegExp(
  emailTemplateTokenContract.slug_normalization.trim_pattern,
  'g',
);

/** Shared with mb-server picker SQL and audience-processor send-time rendering. */
export function slugifyTemplateToken(key: string): string {
  return String(key)
    .toLowerCase()
    .replace(
      SLUG_NON_ALPHANUMERIC_RE,
      emailTemplateTokenContract.slug_normalization.replacement,
    )
    .replace(SLUG_TRIM_RE, '');
}

export interface CsvAttributeTokenCollision {
  kind: 'reserved' | 'custom_slug';
  policy: CsvTemplateTokenPolicy;
  token: string;
  columns: string[];
}

// Reserved names are safe to import because send-time rendering has deterministic
// canonical/system precedence. Only two distinct attribute headers collapsing to the
// same token are ambiguous and must block the import.
export const isBlockingCsvAttributeTokenCollision = (
  collision: CsvAttributeTokenCollision,
): boolean => collision.kind === 'custom_slug';

/**
 * Detect free-form CSV columns that would share a deterministic template token with
 * a core field or with another custom column. AI mapping keys are first reconciled to
 * the actual headers, so casing/punctuation drift cannot bypass the check.
 */
export function findCsvAttributeTokenCollisions(
  headers: string[],
  fieldMappings: Record<string, string>,
): CsvAttributeTokenCollision[] {
  const resolved = resolveHeaderMappings(headers, fieldMappings);
  const columnsByToken = new Map<string, string[]>();

  headers.forEach((header) => {
    if (resolved[header] !== 'attributes') return;
    const token = slugifyTemplateToken(header);
    if (!token) return;
    columnsByToken.set(token, [...(columnsByToken.get(token) || []), header]);
  });

  const collisions: CsvAttributeTokenCollision[] = [];
  [...columnsByToken.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([token, columns]) => {
      const sortedColumns = [...new Set(columns)].sort();
      const policy = TEMPLATE_TOKEN_POLICY_BY_NAME.get(token);
      if (policy) {
        collisions.push({
          kind: 'reserved',
          policy,
          token,
          columns: sortedColumns,
        });
      }
      if (sortedColumns.length > 1) {
        collisions.push({
          kind: 'custom_slug',
          policy: 'block',
          token,
          columns: sortedColumns,
        });
      }
    });

  return collisions;
}

export function formatCsvAttributeTokenCollisionError(
  collisions: CsvAttributeTokenCollision[],
): string {
  const details = collisions
    .map(({ token, columns }) => `${columns.map((column) => `"${column}"`).join(' and ')} → {{${token}}}`)
    .join('; ');
  return `Custom field mapping has email-template token collisions: ${details}. Map each column to a canonical or dynamic field, skip it, or rename the CSV header.`;
}

// Standard field names for person type - matches backend requirements exactly
export const PERSON_STANDARD_FIELDS = [
  'first_name',
  'last_name',
  'email',
  'company_name',
  'phone',
  'job_title',
  'website', // optional, auto-generated from email domain
  'linkedin_url', // optional
  'hs_record_id', // HubSpot record identifier
  'sf_record_id', // Salesforce record identifier
  'industry', // dedicated DB column — prefer over dynamic_col_1
  'dynamic_col_1', // for additional custom fields (e.g. language, country, custom signals)
  'dynamic_col_2',
  'dynamic_col_3',
  'dynamic_col_4',
  'attributes', // free-form JSONB catch-all — multiple columns may map here; column name becomes the key
] as const;

// Standard field names for company type - matches backend requirements exactly
export const COMPANY_STANDARD_FIELDS = [
  'company_name',
  'phone',
  'website', // optional, auto-generated from email domain
  'address',
  'city',
  'state',
  'contact_first_name',
  'contact_last_name',
  'email',
  'job_title',
  'linkedin_url', // optional
  'hs_record_id', // HubSpot record identifier
  'sf_record_id', // Salesforce record identifier
  'industry', // dedicated DB column — prefer over dynamic_col_1
  'dynamic_col_1', // for additional custom fields (e.g. language, country, custom signals)
  'dynamic_col_2',
  'dynamic_col_3',
  'attributes', // free-form JSONB catch-all — multiple columns may map here; column name becomes the key
] as const;

type PersonField = (typeof PERSON_STANDARD_FIELDS)[number];
type CompanyField = (typeof COMPANY_STANDARD_FIELDS)[number];

/**
 * Whether a mapped row carries a usable way to reach the person.
 *
 * Shared with the person-type filter inside {@link applyCsvMapping} so the two can never
 * disagree. Email must be well-formed — a non-empty-but-invalid value ("N/A", "-") is not
 * a contact method — while phone and LinkedIn only need to be present, matching the
 * backend's `validatePersonRecord`.
 */
export function rowHasContactMethod(row: {
  email?: unknown; phone?: unknown; linkedin_url?: unknown;
}): boolean {
  if (EMAIL_REGEX.test(String(row.email ?? '').trim())) return true;
  if (String(row.phone ?? '').trim() !== '') return true;
  return String(row.linkedin_url ?? '').trim() !== '';
}

/**
 * A row that names a human. That is the whole test for whether a file is about people.
 *
 * The importer used to reject a named person carrying no email, phone or LinkedIn, which is
 * what turned a ranked prospect list into a company audience and lost the people it was about
 * (Sunder, 2026-09-16: "The Jumio already has people. It should not import as company. It
 * should import as people and add email column so we can enrich").
 *
 * A first cut of this required a company alongside the name, on the theory that a bare name
 * cannot be enriched. True, and not the importer's call to make: a file that names people is a
 * people file, and refusing the whole upload teaches the user nothing except that the product
 * rejected their data. Andi, 2026-09-16: "if the csv have person even not email we categorize
 * them as people."
 */
export function rowNamesAPerson(row: { first_name?: unknown; last_name?: unknown }): boolean {
  return String(row.first_name ?? '').trim() !== '' || String(row.last_name ?? '').trim() !== '';
}

/** Reachable now, or at least a named human — the test the person-type import actually applies. */
export function rowIsImportablePerson(
  row: Parameters<typeof rowHasContactMethod>[0] & Parameters<typeof rowNamesAPerson>[0],
): boolean {
  return rowHasContactMethod(row) || rowNamesAPerson(row);
}

/**
 * True when a file that names people will import as a *company* audience because not one
 * row carries a way to reach anyone.
 *
 * This is the most confusing outcome of the importer ("i am trying to import this audience
 * but its only importing as company" — a 100-row prospect list of names, titles and company
 * domains with no email column). It is working as designed: a person row is rejected unless
 * it carries email, phone or LinkedIn, enforced independently by `applyCsvMapping` below, by
 * `uploadCsv` in the controller, and by `validatePersonRecord` at the DB layer. The type is
 * not the user's mistake to fix, so the review step says so before the import rather than
 * explaining it in a toast after.
 *
 * Reads the rows `applyCsvMapping` actually produced rather than re-deriving intent from
 * `detected_type` and the column mapping, because neither survives contact with real files:
 *   - `detected_type` is `'person'` on the route that surprises people most, where every row
 *     is dropped for want of a contact method and the file is reinterpreted as `company`
 *     below — the UI said "person" right up to the import.
 *   - A column *mapped* to `email` may be empty, or hold "N/A", in every row. Only the
 *     produced rows show whether anyone is actually reachable.
 * Reachability is judged by the same {@link rowHasContactMethod} the import itself uses, so
 * the warning cannot drift from the decision it is describing.
 */
export function isPeopleListWithoutContactMethod(
  result: { type: 'person' | 'company'; data: Record<string, unknown>[] } | null,
): boolean {
  // Now that a named person at a named company imports AS a person, the surprise this warns
  // about is no longer "you got a company audience" — it is "nobody in this file is reachable
  // yet". Both shapes still qualify: a person import with no contacts, and a genuine company
  // fallback for rows too thin to enrich.
  if (!result) return false;
  let namesSomeone = false;
  for (const row of result.data) {
    // One reachable row is enough: the file is not contact-less, so this is not the case.
    if (rowHasContactMethod(row)) return false;
    // contact_* is what both routes produce: the AI maps names straight there for a
    // company file, and the fallback rewrites first_name/last_name on the way. The raw
    // spelling is checked too — it costs nothing, and missing it would silently turn the
    // warning off rather than fail loudly.
    if (row.contact_first_name || row.contact_last_name || row.first_name || row.last_name) {
      namesSomeone = true;
    }
  }
  // A pure target-account list (companies, no people named) is not a surprise — the user
  // uploaded companies and got companies.
  return namesSomeone;
}

/**
 * Generate AI prompt for CSV field mapping
 */
export function generateCsvMappingPrompt(
  csvHeaders: string[],
  csvSampleRows: string[][],
  originalFileName?: string,
  /**
   * When appending to an EXISTING audience, the destination table (person vs company)
   * is already fixed. Pass it here so the AI is constrained to that type instead of
   * guessing — a wrong guess would route rows into the other table and orphan them.
   */
  forcedType?: 'person' | 'company',
): string {
  // Check if headers look like actual data (no headers provided).
  // Match common field tokens anywhere in the header (word-boundary), so variants
  // like "Work Email", "E-mail", "Full Name", "Organisation", "Mobile" are still
  // recognized as real headers. A too-narrow "starts-with" check mis-flagged these
  // as headerless, which made the AI emit Column_N keys and the parser then treated
  // the header row itself as a data row.
  const HEADER_HINT =
    /\b(name|e-?mail|mail|phone|mobile|cell|company|organi[sz]ation|org|title|role|job|first|last|full|address|city|state|country|region|website|url|linkedin|industry|contact)\b/i;
  const hasRealHeaders = csvHeaders.some((header) => HEADER_HINT.test(header));

  // Calculate file statistics for context
  const totalRows = csvSampleRows.length;
  const totalColumns = csvHeaders.length;
  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short' });

  let promptContent: string;

  if (hasRealHeaders) {
    // CSV has proper headers
    const sampleData = csvSampleRows.map((row) =>
      Object.fromEntries(csvHeaders.map((header, i) => [header, row[i] || ''])),
    );

    promptContent = `CSV File Information:
- Original filename: ${originalFileName || 'unknown.csv'}
- Total columns: ${totalColumns}
- Sample size: ${totalRows} rows (showing first 10)
- Upload date: ${currentDate}

CSV Headers: ${csvHeaders.join(', ')}

Sample Data (first 10 rows):
${JSON.stringify(sampleData, null, 2)}`;
  } else {
    // CSV has no headers - headers are actually data
    const allRows = [csvHeaders, ...csvSampleRows];
    const sampleData = allRows.map((row, index) =>
      Object.fromEntries(row.map((cell, i) => [`Column_${i + 1}`, cell || ''])),
    );

    promptContent = `CSV File Information:
- Original filename: ${originalFileName || 'unknown.csv'}
- Total columns: ${totalColumns}
- Sample size: ${totalRows} rows (showing first 10)
- Upload date: ${currentDate}

CSV has NO HEADERS - analyzing raw data rows:

Sample Data (first 10 rows):
${JSON.stringify(sampleData, null, 2)}

IMPORTANT: You must infer what each column represents based on the data patterns.`;
  }

  const forcedTypeDirective = forcedType
    ? `\n🔒 LOCKED TYPE: This data is being appended to an existing ${forcedType.toUpperCase()} audience. You MUST set "detected_type" to "${forcedType}" and map columns using ${forcedType}-type fields ONLY. Do NOT use the other type's fields. Ignore any signals that suggest the other type.\n`
    : '';

  return `TASK: Analyze CSV data and return ONLY a JSON object for field mapping.

${promptContent}
${forcedTypeDirective}
🚨 CRITICAL: RESPOND WITH ONLY JSON - NO OTHER TEXT 🚨

DO NOT include:
- Explanations before or after JSON
- Markdown code blocks
- Commentary or analysis
- Any text that is not part of the JSON object

START your response with { and END with }

Required JSON structure (copy this exact format):

{
  "detected_type": "person",
  "field_mappings": {
    // ⚠️ CRITICAL: Each standard_field value can ONLY appear ONCE in this object
    // ❌ WRONG: "Website": "dynamic_col_3", "Dynamic Column 3": "dynamic_col_3" (DUPLICATE!)
    // ✅ CORRECT: "Website": "website", "Dynamic Column 3": "dynamic_col_3"
    "Column Name": "standard_field"
  },
  "confidence": 0.95,
  "reasoning": "Brief explanation",
  "suggested_filename": "contextual_filename.csv",
  "suggested_title": "Descriptive Title for This Data"
}

⚠️ DUPLICATE CHECK: Before responding, verify NO standard_field value is repeated in field_mappings.

REPLACE VALUES with appropriate mappings based on the CSV data.

FILENAME/TITLE GUIDELINES:
- suggested_filename: Create a descriptive filename based on:
  • Data content (e.g., job titles, company names, industries)
  • Original filename context (if meaningful)
  • Date context (current or from data)
  • Examples: "sales_team_contacts_dec_2024.csv", "tech_startups_california.csv", "marketing_leads_q4_2024.csv"
- suggested_title: Create a professional description that includes:
  • What the data represents
  • Key characteristics (industry, location, department)
  • Total count or date range if apparent
  • Examples: "Sales Team Contact List - December 2024 (150 contacts)", "California Tech Startups Database", "Q4 2024 Marketing Qualified Leads"
- IMPORTANT: Analyze the actual data values to determine context:
  • Look at email domains for company context
  • Check job titles for department/industry hints
  • Review company names for industry patterns
  • Use location data if present

ALLOWED FIELD MAPPINGS:

For PERSON type ONLY use:
✅ first_name, last_name, email, company_name, phone, job_title, website, linkedin_url
✅ hs_record_id, sf_record_id (CRM identifiers — map these directly, do NOT put in dynamic columns)
✅ industry (dedicated column — map directly when CSV has an industry/vertical column)
✅ dynamic_col_1, dynamic_col_2, dynamic_col_3, dynamic_col_4 (reserve for high-priority signals: media bg, language, country)
✅ attributes (free-form JSONB catch-all — map ALL other useful columns here; the column name becomes the key)

For COMPANY type ONLY use:
✅ company_name, phone, website, address, city, state, contact_first_name, contact_last_name, email, job_title, linkedin_url
✅ hs_record_id, sf_record_id (CRM identifiers — map these directly, do NOT put in dynamic columns)
✅ industry (dedicated column — map directly when CSV has an industry/vertical column)
✅ dynamic_col_1, dynamic_col_2, dynamic_col_3 (reserve for high-priority signals: media bg, language, country)
✅ attributes (free-form JSONB catch-all — map ALL other useful columns here; the column name becomes the key)

❌ DO NOT use any other field names

ATTRIBUTES FIELD (use generously — this is how rich CSVs keep their data):
- 'attributes' is a JSONB bag. Each column you map to it is preserved with its original header as the key.
- ⚠️ UNLIKE every other field, 'attributes' CAN be used by MULTIPLE columns — this is intentional and required.
- Map to attributes: blog/article URLs, article titles, competitor status, lead source, custom notes, tags,
  any extra column that carries business value but has no dedicated standard field.
- When there are more useful columns than free dynamic_col slots, do NOT discard them — map them ALL to attributes.
- Result is stored as: { "Blog URL": "https://...", "Competitor Status": "Response", "Article Title": "..." }
- Example: "Blog URL" → "attributes", "Competitor Status" → "attributes", "Article Title" → "attributes" ✅

DYNAMIC COLUMNS USAGE (reserve for the few signals you want to sort/route on directly):
- Use dynamic_col_1/2/3 sparingly — prefer 'attributes' for general extra data.
- Media/background content → dynamic_col_3 (used for HeyGen per-prospect backgrounds):
  • Column names indicating media: video_url, background_video_url, background_url, background, video, video_link
  • Supports both video (.mp4, .mov, .avi, .webm, .mkv, .m4v) and image (.jpg, .png, .gif, .webp) backgrounds
  • If column has a standard field name (e.g., "Website", "Logo") → map to that field, NOT dynamic_col_3
- Language / locale → dynamic_col_1; Country / region → dynamic_col_1 (or _2 if language uses _1)
- Everything else of value → attributes (not dynamic columns)

${
  hasRealHeaders
    ? 'Use the actual CSV column names as keys in field_mappings.'
    : 'Use Column_1, Column_2, etc. as keys in field_mappings based on the data analysis.'
}

MAPPING RULES:
1. Map ONLY columns that exactly match allowed fields
2. Skip only internal/system metadata with no business value: internal numeric IDs (row_id, record_id unless HubSpot/Salesforce), timestamps (created_at, updated_at), binary status flags (is_active, synced), and columns that are clearly processing artifacts. DO NOT skip country, language, region, or any locale signal, and DO NOT skip article titles, blog URLs, competitor status, notes, or other business context — map these to attributes.
3. ⚠️ CRITICAL: Each standard field can ONLY be used ONCE - NO DUPLICATES
   - NEVER map two different columns to the same standard field
   - Example: If "Website" → website, then "Dynamic Column 3" CANNOT → website
   - EXCEPTION: "attributes" is the ONLY field that allows multiple columns — each becomes a key in the JSONB bag
   - Example: "Blog URL" → "attributes", "Article Title" → "attributes", "Competitor Status" → "attributes" ✅ (all allowed)
4. Map columns by NAME, not by content value:
   - If column NAME matches a standard field (e.g., "Website"), map to that field EVEN IF the cell is empty
   - Empty cells are OK - the system handles them
   - Examples:
     ✅ "Website" (empty value) → website (NOT dynamic_col_3, even if empty)
     ✅ "Email" (has value) → email
     ✅ "Background Video URL" or "Background URL" (any media) → dynamic_col_3
     ✅ "Dynamic Column 3" (contains media URL) → dynamic_col_3
     ❌ "Website" (empty) → dynamic_col_3 (WRONG - map by name, not emptiness)
     ❌ "Logo URL" → dynamic_col_3 (WRONG - this is not a background, skip or use dynamic_col_1/2)
   - ⚠️ EXCEPTION — CONTACT FIELDS (email, phone): when MULTIPLE columns compete for the same
     contact field, this is the ONE case where you MUST look at the SAMPLE DATA. A person row is
     DROPPED if its final email is empty, so the canonical "email" MUST come from the column that
     is actually POPULATED. Map the populated one to "email"; map the rest to attributes.
     • Inspect the sample rows: which email column has real values vs is mostly blank?
     • Map the POPULATED email column → email. Map empty/secondary email columns → attributes.
     • Example: "Author Email" is blank but "Work Email" / "Final Email" are populated →
       "Final Email" → email, "Work Email" → attributes, "Author Email" → attributes.
     • NEVER map an empty email column to "email" when another email column has data — that drops every row.
5. Use "person" for individual contacts, "company" for business data
   
   🚨 IMPORTANT - Detect "company" type when:
   
   CASE 1: CSV has location fields (address, city, state, phone)
   → Even if no person names present
   → This is business/organization data (office locations, branches, stores)
   → Map to company table: company_name, phone, website, address, city, state
   
   CASE 2: CSV has NO person names at all (only organisation rows)
   → Business directory / target-account list
   → Map company/organization to company_name

   🚨 A FILE THAT NAMES PEOPLE IS A PEOPLE FILE, even with no email column.
   A ranked prospect list — Name, Title, Company, Company domain, no email — is the single most
   common shape here, and calling it "company" throws away the thing the user uploaded it for.
   Those rows import as people with an empty email, which is exactly what the email/LinkedIn
   finders are built to fill in. Use "person", map names to first_name/last_name, the employer to
   company_name, and a company/domain column to "website" — on a person row that IS the company
   domain (stored in the website column; there is no company_domain person field). Enrichment resolves an
   address from the name plus that domain, so dropping it into a custom attribute wastes it.
   Only treat a named-people file as "company" when the names are incidental to organisation
   records (CASE 1 above: addresses, branches, store locations).

   Otherwise: Use "person" type for individual contacts
5. Set detected_type to "person" or "company"
6. Confidence: 0.9+ for clear matches, 0.7+ for reasonable matches

EXAMPLE RESPONSE (copy this format exactly):

EXAMPLE 1 - Person Type (has first_name/last_name + email):
{
  "detected_type": "person",
  "field_mappings": {
    "Account Name": "company_name",
    "Title": "job_title",
    "First Name": "first_name",
    "Last Name": "last_name",
    "Email": "email",
    "Industry": "industry",
    "Language": "dynamic_col_1",
    "Country": "dynamic_col_2"
  },
  "confidence": 0.95,
  "reasoning": "Clear person data. Industry maps to dedicated column. Language and Country map to dynamic columns as locale signals for email personalization.",
  "suggested_filename": "enterprise_sales_contacts_dec_2024.csv",
  "suggested_title": "Enterprise Sales Contacts - December 2024 (Regional Managers)"
}

EXAMPLE 2 - Company Type (has address/city/state - business locations):
{
  "detected_type": "company",
  "field_mappings": {
    "Company Name": "company_name",
    "Phone": "phone",
    "Website": "website",
    "Street Address": "address",
    "City": "city",
    "State": "state"
  },
  "confidence": 0.95,
  "reasoning": "CSV contains business location fields (address, city, state) indicating company/organization data (office locations, branches, stores). No personal contact fields present.",
  "suggested_filename": "tech_company_offices_california.csv",
  "suggested_title": "California Tech Company Office Locations"
}

EXAMPLE 3 - Person Type (has First Name/Last Name, NO email — an enrichment target):
{
  "detected_type": "person",
  "field_mappings": {
    "Name": "first_name",
    "Title": "job_title",
    "Company": "company_name",
    "Company domain": "website",
    "Rank": "dynamic_col_1"
  },
  "confidence": 0.95,
  "reasoning": "CSV names real people with titles at named companies and carries no email column. That is a prospect list to enrich, not a business directory - importing it as company would discard the people the file is about. Person type with an empty email; the company domain goes to website so the finders can resolve an address from name + domain.",
  "suggested_filename": "ranked_prospects_sep_2026.csv",
  "suggested_title": "Ranked Prospects - September 2026"
}

EXAMPLE 4 - Person Type with many extra columns (use attributes for the overflow):
{
  "detected_type": "person",
  "field_mappings": {
    "Company Name": "company_name",
    "Company Domain": "website",
    "Author Name": "first_name",
    "Work Email": "email",
    "Job Title": "job_title",
    "Blog URL": "attributes",
    "Competitor Status": "attributes",
    "Normalized Article Title": "attributes"
  },
  "confidence": 0.92,
  "reasoning": "Person data with email and job title. Extra columns (blog URL, article title, competitor status) carry business value but have no dedicated field, so they map to attributes — the free-form JSONB bag. Multiple columns map to attributes without conflict.",
  "suggested_filename": "iot_author_contacts_jun_2026.csv",
  "suggested_title": "IoT Blog Author Contacts with Article Context - June 2026"
}

⚠️  REMEMBER: Start with { and end with } - NO OTHER TEXT!`;
}

/**
 * Parse CSV content and extract headers + sample rows for AI analysis
 */
export function extractCsvSample(csvContent: string): {
  headers: string[];
  sampleRows: string[][];
} {
  const rows = parseCsvRows(csvContent);

  if (rows.length === 0) {
    throw new Error('CSV file is empty');
  }

  const headers = rows[0];
  const sampleRows = rows.slice(1, 11);

  return { headers, sampleRows };
}

/**
 * Split a full name held in `firstField` into `firstField` + `lastField` when the
 * last field is empty. First whitespace-delimited token becomes the first name, the
 * remainder becomes the last name ("Ashari Ahmed Martin" -> "Ashari" / "Ahmed Martin").
 * No-op when the last field is already populated (real First/Last uploads untouched)
 * or the first field is single-token / empty.
 */
function splitFullNameInto(row: any, firstField: string, lastField: string): void {
  const value = typeof row[firstField] === 'string' ? row[firstField].trim() : '';
  const existingLast = typeof row[lastField] === 'string' ? row[lastField].trim() : '';
  if (!value || existingLast) return;
  const spaceIdx = value.indexOf(' ');
  if (spaceIdx === -1) return;
  row[firstField] = value.slice(0, spaceIdx).trim();
  row[lastField] = value.slice(spaceIdx + 1).trim();
}

/**
 * Apply AI field mapping to CSV data
 */
export function applyCsvMapping(
  csvContent: string,
  mapping: CsvFieldMapping,
): { type: 'person' | 'company'; data: any[] } {
  const rows = parseCsvRows(csvContent);

  if (rows.length < 1) {
    throw new Error('CSV must contain at least one data row');
  }

  // Check if mapping uses Column_1, Column_2 format (headerless CSV)
  const usesColumnFormat = Object.keys(mapping.field_mappings).some((key) =>
    key.match(/^Column_\d+$/),
  );

  let dataRows: string[][];
  let headers: string[];

  if (usesColumnFormat) {
    // No headers - all rows are data
    dataRows = rows;
    headers = rows[0]?.map((_, index) => `Column_${index + 1}`) || [];
  } else {
    // Has headers - first row is headers
    headers = rows[0];
    dataRows = rows.slice(1);
  }

  // Resolve every header to a standard field once (so containment-uniqueness holds
  // across the full header set), then apply the mapping to convert rows.
  // 'attributes' is a catch-all JSONB field: when one or more CSV columns map to
  // 'attributes', each column's original header becomes a key inside row.attributes.
  const resolved = resolveHeaderMappings(headers, mapping.field_mappings);
  const blockingTokenCollisions = findCsvAttributeTokenCollisions(
    headers,
    mapping.field_mappings,
  ).filter(isBlockingCsvAttributeTokenCollision);
  // Last-resort guard for non-interactive paths such as chat CSV uploads. The audience
  // mapping modal validates these inline before upload so users can fix the columns.
  if (blockingTokenCollisions.length > 0) {
    throw new Error(formatCsvAttributeTokenCollisionError(blockingTokenCollisions));
  }

  // Indices of columns whose header looks like an email (Work Email, Final Email, …).
  // Used to rescue the canonical `email` when the AI maps an EMPTY email column to
  // `email` and routes the populated one into attributes — which would otherwise drop
  // every row as "missing email/phone/LinkedIn".
  const emailHeaderIndices = headers
    .map((h, i) => (/e-?mail/i.test(h) ? i : -1))
    .filter((i) => i >= 0);

  // Build + filter rows for a given detected type against a given header->field
  // resolution. Factored out so the person-type pass and the company-type fallback
  // pass (below) share identical row-construction/rescue/filter logic.
  const buildRows = (detectedType: 'person' | 'company', headerResolution: { [header: string]: string }) =>
    dataRows
      .map((row) => {
        const mappedRow: any = {};

        headers.forEach((header, index) => {
          const standardField = headerResolution[header];
          if (!standardField || !row[index]) return;
          if (standardField === 'attributes') {
            // Collect into the attributes JSONB bag keyed by the original column name
            if (!mappedRow.attributes) mappedRow.attributes = {};
            mappedRow.attributes[header] = row[index].trim();
          } else {
            mappedRow[standardField] = row[index].trim();
          }
        });

        // Full-name split: the AI mapper has no `full_name` target and maps a single
        // name column to `first_name`, leaving `last_name` empty — so an uploaded
        // "Full Name" column (e.g. "Terry Lee") lands entirely in first_name. When a
        // name field holds a multi-token value and its paired last-name field is empty,
        // split on the first space (first token → first name, remainder → last name),
        // matching the convention used across the backend (websiteVisitImportUtils,
        // lusha). Never touches rows that already provided a separate last name.
        splitFullNameInto(mappedRow, 'first_name', 'last_name');
        if (detectedType === 'company') {
          splitFullNameInto(mappedRow, 'contact_first_name', 'contact_last_name');
        }

        // Email rescue (person only): if the mapped email is empty/invalid, pull the first
        // VALID email from (1) any email-named column, (2) the attributes bag, (3) any other
        // column — so a row with a real email somewhere is never dropped due to a poor AI
        // column pick (incl. first-wins dedup dropping a populated, non-email-named column).
        // If no valid email is found, BLANK the field so an invalid value (e.g. "not-an-email")
        // is never counted as a contact method nor stored as garbage.
        if (detectedType === 'person' && !EMAIL_REGEX.test(mappedRow.email || '')) {
          let rescued = '';
          for (const idx of emailHeaderIndices) {
            const val = (row[idx] || '').trim();
            if (EMAIL_REGEX.test(val)) { rescued = val; break; }
          }
          if (!rescued && mappedRow.attributes) {
            for (const val of Object.values(mappedRow.attributes)) {
              if (EMAIL_REGEX.test(String(val))) { rescued = String(val); break; }
            }
          }
          if (!rescued) {
            for (let i = 0; i < row.length; i += 1) {
              const val = (row[i] || '').trim();
              if (EMAIL_REGEX.test(val)) { rescued = val; break; }
            }
          }
          mappedRow.email = rescued; // valid rescued email, or '' (drops garbage)
        }

        return mappedRow;
      })
      .filter((row) =>
        // Filter out empty rows
        Object.values(row).some((value) => String(value).length > 0),
      )
      .filter((row) => {
        // Filter invalid records before API call to avoid constraint violations.
        // Mirrors backend validatePersonRecord: a person needs at least one contact
        // method — email, phone, OR linkedin_url (LinkedIn-only lists are valid for
        // LinkedIn outreach). Company needs company_name. Email must be well-formed —
        // a non-empty-but-invalid email is not a usable contact method.
        if (detectedType === 'person') {
          // Reachable now, or a named person at a named company we can go and find.
          return rowIsImportablePerson(row);
        } else {
          return row.company_name && row.company_name.trim() !== '';
        }
      });

  const mappedData = buildRows(mapping.detected_type, resolved);

  if (mapping.detected_type === 'person' && mappedData.length === 0) {
    // Target-account / persona lists — company name + job title, no named contact
    // yet — have no email/phone/LinkedIn to satisfy the person-type filter above, so
    // every row would otherwise be dropped and the whole file rejected as "no rows
    // could be imported". These rows are legitimate: they're the exact shape "Find
    // People" (audience-detail page, company audiences only) is built to enrich. Only
    // reinterpret when the file actually has company data to fall back to — a truly
    // empty/malformed file still hits the person-type error the caller shows.
    const hasCompanyNameColumn = Object.values(resolved).includes('company_name');
    if (hasCompanyNameColumn) {
      // first_name/last_name have no meaning on a company record — a company's own
      // named contact lives in contact_first_name/contact_last_name instead. Every
      // other standard field (email, phone, website, linkedin_url, job_title, CRM
      // IDs, industry, dynamic columns, attributes) is shared verbatim between the
      // person and company schemas, so only these two keys need translating.
      const companyResolution = Object.fromEntries(
        Object.entries(resolved).map(([header, field]) => [
          header,
          field === 'first_name' ? 'contact_first_name' : field === 'last_name' ? 'contact_last_name' : field,
        ]),
      );
      const companyFallbackData = buildRows('company', companyResolution);
      if (companyFallbackData.length > 0) {
        return { type: 'company', data: companyFallbackData };
      }
    }
  }

  return {
    type: mapping.detected_type,
    data: mappedData,
  };
}

/**
 * Generate unique session ID for CSV mapping requests
 */
export function generateCsvMappingSessionId(): string {
  return uuidv4(); // Standard UUID format without prefix
}

/**
 * Validate AI mapping response
 */
export function validateCsvMapping(response: any): CsvFieldMapping {
  if (!response || typeof response !== 'object') {
    throw new Error('Invalid mapping response format');
  }

  if (!['person', 'company'].includes(response.detected_type)) {
    throw new Error('Invalid detected_type. Must be "person" or "company"');
  }

  if (!response.field_mappings || typeof response.field_mappings !== 'object') {
    throw new Error('Invalid field_mappings format');
  }

  if (
    typeof response.confidence !== 'number' ||
    response.confidence < 0 ||
    response.confidence > 1
  ) {
    throw new Error('Invalid confidence value. Must be between 0 and 1');
  }

  // Validate field mappings against standard fields; deduplicate by first-wins.
  // The AI sometimes over-assigns dynamic_col_N slots (more columns than available
  // slots) which previously threw and wiped all valid mappings. Now we keep the first
  // occurrence of each standard field and silently drop later ones so the core
  // mappings (email, name, company, etc.) always survive.
  //
  // 'attributes' is the ONE exception to dedup: it is a free-form JSONB bag where each
  // mapped column's original name becomes a key, so multiple columns MAY map to it.
  const validFields =
    response.detected_type === 'person' ? PERSON_STANDARD_FIELDS : COMPANY_STANDARD_FIELDS;
  const usedFields = new Set<string>();
  const deduped: Record<string, string> = {};

  for (const [csvField, standardField] of Object.entries(response.field_mappings)) {
    if (!validFields.includes(standardField as any)) {
      // Skip unrecognized fields rather than aborting the whole mapping
      continue;
    }
    if (standardField !== 'attributes' && usedFields.has(standardField as string)) {
      // First occurrence wins — later duplicates are dropped (attributes allows multi-map)
      continue;
    }
    usedFields.add(standardField as string);
    deduped[csvField] = standardField as string;
  }

  return { ...response, field_mappings: deduped } as CsvFieldMapping;
}
